import Section from '@/components/shared/Section';
import { Button } from '@/components/ui/button';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useStripe } from '@/hooks/useStripe';
import { useSubscriptionFlow } from '@/hooks/useSubscriptionFlow';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface PricingProps {
  newTeam?: any;
}

export const Pricing = ({ newTeam }: PricingProps) => {
  const { products, createCheckoutSession } = useStripe();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const { handleSubscriptionComplete } = useSubscriptionFlow({ newTeam });

  const defaultProduct = products.data?.[0];
  const yearlyPrice = defaultProduct?.prices.find(
    (price) => price.recurring?.interval === 'year',
  );
  const monthlyPrice = defaultProduct?.prices.find(
    (price) => price.recurring?.interval === 'month',
  );
  const selectedPrice = interval === 'year' ? yearlyPrice : monthlyPrice;

  const yearlyDiscount =
    monthlyPrice && yearlyPrice
      ? Math.round(
          ((monthlyPrice.unit_amount! * 12 - yearlyPrice.unit_amount!) /
            (monthlyPrice.unit_amount! * 12)) *
            100,
        )
      : 0;

  const handleCheckout = async (priceId: string) => {
    const response = await createCheckoutSession.mutateAsync({
      priceId,
      quantity: 1,
      returnUrl: window.location.href,
    });

    if (response.url) {
      await handleSubscriptionComplete();
      window.location.href = response.url;
    }
  };

  return (
    <Section id="pricing">
      <div className="grid grid-rows-1">
        <div className="grid grid-rows-1 gap-y-10 p-10">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold ter text-balance">
              Simple pricing, no surprises
            </h2>
            <p className="mt-6 text-balance text-muted-foreground">
              Choose an <strong>affordable plan</strong> that&apos;s packed with
              the best features for engaging your audience, creating customer
              loyalty, and driving sales.
            </p>
          </div>

          <Tabs
            value={interval}
            onValueChange={(value) => setInterval(value as 'month' | 'year')}
            className="mx-auto w-full max-w-md">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="month">Monthly</TabsTrigger>
              <TabsTrigger value="year">
                Yearly
                <span className="ml-2 text-xs font-semibold text-green-500">
                  Save {yearlyDiscount}%
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-3 `}>
          {products.data?.map((product) => {
            const price = (selectedPrice?.unit_amount ?? 0) / 100;

            return (
              <div
                key={product.id}
                className="flex flex-col h-full border-t last:border-r-0 lg:col-start-2">
                <CardHeader className="border-b p-4">
                  <CardTitle className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      {product.name}
                    </span>
                  </CardTitle>
                  <div className="pt-2 flex items-center text-3xl font-bold">
                    <motion.div
                      key={selectedPrice?.id}
                      initial={{
                        opacity: 0,
                        x: interval === 'year' ? -10 : 10,
                      }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}>
                      £{price.toLocaleString('en-Gb')}
                      <span className="text-sm font-medium text-muted-foreground">
                        / {interval}
                      </span>
                    </motion.div>
                  </div>
                  <p className="text-[15px] font-medium text-muted-foreground">
                    {product.description}
                  </p>
                </CardHeader>

                <CardContent className="flex-grow p-4">
                  <Button
                    className="w-full"
                    isLoading={createCheckoutSession.isPending}
                    onClick={() => handleCheckout(selectedPrice?.id ?? '')}>
                    Subscribe
                  </Button>
                </CardContent>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
